import { useEffect, useState } from "react";
import { endpoints } from "../lib/api";

const IndicatorRow = ({ label, value, signal }) => {
  const color =
    signal === 1
      ? "text-signal-buy"
      : signal === -1
      ? "text-signal-sell"
      : "text-muted-foreground";
  const arrow = signal === 1 ? "▲" : signal === -1 ? "▼" : "◆";
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
      <span className="font-mono text-[11px] uppercase text-muted-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <span className="font-mono text-[12px]">{value}</span>
        <span className={`font-mono text-[10px] ${color}`}>{arrow}</span>
      </div>
    </div>
  );
};

export const SignalPanel = ({ symbol, interval = "15m" }) => {
  const [sig, setSig] = useState(null);

  const load = async () => {
    if (!symbol) return;
    try {
      const { data } = await endpoints.signals(symbol, interval);
      setSig(data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [symbol, interval]); // eslint-disable-line

  if (!sig)
    return <div className="border border-border p-4 rounded-sm bg-[#0d0d0d]">Loading signals...</div>;

  const s = sig.signals || {};
  const scoreClass =
    sig.decision === "BUY"
      ? "text-signal-buy"
      : sig.decision === "SELL"
      ? "text-signal-sell"
      : "text-muted-foreground";
  const bar = Math.min(100, Math.abs(sig.score));

  return (
    <div
      data-testid="signal-panel"
      className="border border-border p-4 rounded-sm bg-[#0d0d0d]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Composite Signal
          </div>
          <div className={`font-heading font-black text-3xl tracking-tight ${scoreClass}`} data-testid="signal-decision">
            {sig.decision}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] text-muted-foreground uppercase">Score</div>
          <div className={`font-mono font-bold text-2xl ${scoreClass}`} data-testid="signal-score">
            {sig.score}
          </div>
        </div>
      </div>

      <div className="w-full h-1.5 bg-[#151515] mb-4 relative">
        <div
          className={`absolute top-0 h-full ${
            sig.score >= 0 ? "bg-signal-buy left-1/2" : "bg-signal-sell right-1/2"
          }`}
          style={{ width: `${bar / 2}%` }}
        />
        <div className="absolute top-0 left-1/2 w-px h-full bg-border" />
      </div>

      <IndicatorRow label="EMA Trend" value={`${sig.ema9.toFixed(4)}/${sig.ema21.toFixed(4)}`} signal={s.ema_trend} />
      <IndicatorRow label="Price vs EMA21" value={sig.price.toFixed(4)} signal={s.price_ema} />
      <IndicatorRow label="RSI(14)" value={sig.rsi.toFixed(2)} signal={s.rsi} />
      <IndicatorRow label="MACD Hist" value={sig.macd_hist.toFixed(4)} signal={s.macd} />
      <IndicatorRow label="Bollinger" value={`${sig.bb_lower.toFixed(2)}·${sig.bb_upper.toFixed(2)}`} signal={s.bb} />
      <IndicatorRow label="Stoch RSI K/D" value={`${sig.stoch_k.toFixed(1)}/${sig.stoch_d.toFixed(1)}`} signal={s.stoch} />
      <IndicatorRow label="Volume" value={`${(sig.volume/1000).toFixed(1)}k`} signal={s.volume} />
    </div>
  );
};

export default SignalPanel;
